// UI overlay for the Buddha Game.
// Shows phase label and instructions above the 3D scene.
// The circular breath ring is now a 3D object in the scene, not a UI overlay.

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BreathPhaseLabel from './BreathPhaseLabel';
import Instructions from './Instructions';
// Debug panels removed
import ProgressOverlay from './Progress/index';
import Countdown from './Countdown';
import CompletionModal from './CompletionModal';

// Import the game logic to get breathing state
import gameLogic from '../Scene/gameLogic';
import type { GameStatus } from '../Scene/gameLogic';
import Scene from '../Scene';

// ============================================================================
// TYPES
// ============================================================================

export type BreathingPhase =
  | 'inhale'
  | 'holdAfterInhale'
  | 'exhale'
  | 'holdAfterExhale';

export interface BreathingState {
  currentPhase: BreathingPhase;
  phaseProgress: number;
  isActive: boolean;
  cyclesCompleted: number;
  successfulCycles: number;
  progressPercent: number;
  /** Session elapsed time in seconds */
  elapsedSeconds?: number;
  sessionComplete: boolean;
  gameStatus: GameStatus;
}

interface BuddhaGameUIProps {
  /** Optional: external breathing state (for testing or custom integration) */
  externalState?: BreathingState;
  /** Current level index in the game (1-based). Defaults to 1. */
  levelIndex?: number;
  /** Total number of levels available. Defaults to 3. */
  totalLevels?: number;
  /** Whether the user has earned the 14-day streak badge (Level 2 unlock). */
  has14DayStreakBadge?: boolean;
  /** Whether to show breathing guidance overlay (phase label + instructions). */
  showBreathingOverlay?: boolean;
  /** Refreshes user badges from the server; used to prevent stale badge state. */
  refreshUserBadges?: () => Promise<void>;
  /** Whether user badges are currently loading/refetching. */
  userBadgesLoading?: boolean;
}

// ============================================================================
// HOOK: useBreathingState
// ============================================================================
// Polls the game logic for the current breathing state at 60fps.

function useBreathingState(): BreathingState {
  const [state, setState] = useState<BreathingState>({
    currentPhase: 'inhale',
    phaseProgress: 0,
    isActive: false,
    cyclesCompleted: 0,
    successfulCycles: 0,
    progressPercent: 0,
    sessionComplete: false,
    gameStatus: 'idle' as GameStatus,
  });

  const clamp01 = (value: number) => Math.min(Math.max(value, 0), 1);

  const updateState = useCallback(() => {
    const status = Scene.getGameStatus?.();

    if (status?.breathingState && status?.movementState) {
      const { breathingState: liveBreathing } = status;
      const progressPercent = clamp01(status.progressPercent ?? 0);
      const elapsedSeconds = Math.max(0, status.elapsedSeconds ?? 0);

      setState({
        currentPhase: liveBreathing.currentPhase,
        phaseProgress: liveBreathing.phaseProgress,
        isActive: liveBreathing.isActive,
        cyclesCompleted: liveBreathing.cyclesCompleted,
        successfulCycles: status.successfulCycles ?? 0,
        progressPercent,
        elapsedSeconds,
        sessionComplete: !!status.sessionComplete,
        gameStatus: status.status as GameStatus,
      });
      return;
    }
    // Fallback: read directly from gameLogic if scene status is unavailable
    const breathingState = gameLogic.getBreathingState();
    const progressState = gameLogic.getProgressState?.();
    const progressPercent = clamp01(progressState?.progressPercent ?? 0);
    const elapsedSeconds = Math.max(0, progressState?.elapsedSeconds ?? 0);

    setState({
      currentPhase: breathingState.currentPhase,
      phaseProgress: breathingState.phaseProgress,
      isActive: breathingState.isActive,
      cyclesCompleted: breathingState.cyclesCompleted,
      successfulCycles: progressState?.successfulCycles ?? 0,
      progressPercent,
      elapsedSeconds,
      sessionComplete: false,
      gameStatus: (progressState?.status ?? 'idle') as GameStatus,
    });
  }, []);

  useEffect(() => {
    // Use requestAnimationFrame for smooth updates synced to display refresh
    let animationFrameId: number;
    let isRunning = true;

    const tick = () => {
      if (!isRunning) return;
      updateState();
      animationFrameId = requestAnimationFrame(tick);
    };

    // Start the update loop
    tick();

    return () => {
      isRunning = false;
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [updateState]);

  return state;
}

// ============================================================================
// HOOK: useDebugPanelToggle
// ============================================================================
// Handles keyboard toggle (D key) for the debug panel.

// Debug toggle removed

// ============================================================================
// COMPONENT
// ============================================================================

export default function BuddhaGameUI({
  externalState,
  levelIndex = 1,
  totalLevels = 3,
  has14DayStreakBadge = false,
  showBreathingOverlay = true,
  refreshUserBadges,
  userBadgesLoading = false,
}: BuddhaGameUIProps) {
  // Use external state if provided, otherwise poll from game logic
  const internalState = useBreathingState();
  const breathingState = externalState ?? internalState;

  const {
    currentPhase,
    isActive,
    cyclesCompleted,
    successfulCycles,
    gameStatus,
  } = breathingState;

  const isPaused = gameStatus === 'paused';
  const isPlaying = gameStatus === 'playing';
  const canShowControls = gameStatus !== 'idle';

  const [showGoalScreen, setShowGoalScreen] = useState(true);
  const [isDismissingGoal, setIsDismissingGoal] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (gameStatus === 'idle') {
      setShowGoalScreen(true);
      setIsDismissingGoal(false);
      setShowCountdown(false);
    }
  }, [gameStatus]);

  // Debug panel removed

  const handleBegin = useCallback(() => {
    if (isDismissingGoal) return;
    setIsDismissingGoal(true);
    // Don't start game immediately - show countdown first
  }, [isDismissingGoal]);

  const handleCountdownComplete = useCallback(() => {
    setShowCountdown(false);
    Scene.startGame();
  }, []);

  const handleGoalScreenTransitionEnd = useCallback(() => {
    if (isDismissingGoal) {
      setShowGoalScreen(false);
      setShowCountdown(true);
    }
  }, [isDismissingGoal]);

  // Reset is handled by scene functions directly from other UI (completion modal)

  const handlePauseToggle = useCallback(() => {
    if (!isPaused && isPlaying) {
      // Pause and show modal
      Scene.pauseGame();
      setShowPauseModal(true);
    } else if (isPaused) {
      // Resume and hide modal if shown
      Scene.resumeGame();
      setShowPauseModal(false);
    }
  }, [isPaused, isPlaying]);

  const handleContinue = useCallback(() => {
    Scene.resumeGame();
    setShowPauseModal(false);
  }, []);

  const handleQuit = useCallback(() => {
    try {
      Scene.disposeScene();
    } catch (e) {
      void e;
    }
    navigate('/dashboard');
  }, [navigate]);

  const [completionModalReady, setCompletionModalReady] = useState(false);
  const completionRefreshInFlightRef = useRef(false);

  useEffect(() => {
    if (!breathingState.sessionComplete) {
      setCompletionModalReady(false);
      completionRefreshInFlightRef.current = false;
      return;
    }

    if (completionModalReady) return;
    if (completionRefreshInFlightRef.current) return;

    completionRefreshInFlightRef.current = true;

    (async () => {
      try {
        // Ensure badge state is fresh before showing the completion modal.
        if (refreshUserBadges) {
          await refreshUserBadges();
        }
      } finally {
        setCompletionModalReady(true);
        completionRefreshInFlightRef.current = false;
      }
    })();
  }, [breathingState.sessionComplete, completionModalReady, refreshUserBadges]);

  const handleViewBadges = useCallback(() => {
    navigate('/badges');
  }, [navigate]);

  const handleReplayLevel1 = useCallback(() => {
    try {
      Scene.resetGame();
    } catch (e) {
      void e;
    }
    navigate('/buddha-game?level=1');
  }, [navigate]);

  const handlePlayLevel2 = useCallback(() => {
    try {
      Scene.resetGame();
    } catch (e) {
      void e;
    }
    navigate('/buddha-game?level=2');
  }, [navigate]);

  const handlePlayLevel3 = useCallback(() => {
    try {
      Scene.resetGame();
    } catch (e) {
      void e;
    }
    navigate('/buddha-game?level=3');
  }, [navigate]);

  const shouldShowBreathingUI =
    (!showGoalScreen || isDismissingGoal) && !breathingState.sessionComplete;
  const breathingUIActive = isActive && !isPaused && shouldShowBreathingUI;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-30 flex flex-col items-center justify-start"
      style={{
        // Safe area insets for mobile devices
        paddingTop: 'max(env(safe-area-inset-top, 0px), 40px)',
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 20px)',
      }}
    >
      {/* Debug panels removed */}

      {/* Gentle progress HUD for cycles */}
      {!breathingState.sessionComplete && (
        <ProgressOverlay
          cyclesCompleted={successfulCycles}
          progressPercent={breathingState.progressPercent}
          onPauseToggle={handlePauseToggle}
          isPaused={isPaused}
          showPause={canShowControls}
          durationSeconds={Math.round(breathingState.elapsedSeconds ?? 0)}
        />
      )}

      {/* Paused overlay */}
      {isPaused && !showGoalScreen && (
        <div className="pointer-events-none fixed inset-0 z-20 bg-black/40 backdrop-blur-[2px] transition-opacity duration-200" />
      )}

      {/* Pause modal: shown when paused via Pause control */}
      {showPauseModal && (
        <div className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-8 transition-opacity duration-300">
          <div className="w-full max-w-md rounded-2xl bg-white/10 p-6 text-center shadow-2xl ring-1 ring-white/10 backdrop-blur-lg">
            <div className="text-center text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
              Paused
            </div>
            <h2 className="mt-2 text-center text-3xl font-semibold text-white">
              Take a break
            </h2>
            <p className="mt-4 text-center text-base leading-relaxed text-white/80">
              You can continue your breathing session or quit to return to the
              dashboard.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={handleContinue}
                className="min-h-[56px] flex-1 rounded-xl bg-gradient-to-r from-amber-400/90 to-orange-400/90 px-6 py-3 text-lg font-semibold uppercase tracking-wide text-white shadow-lg transition-transform duration-200 hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-amber-300/60"
              >
                Continue
              </button>
              <button
                type="button"
                onClick={handleQuit}
                className="min-h-[56px] w-[120px] rounded-xl bg-white/10 px-4 py-3 text-lg font-semibold uppercase tracking-wide text-white/80 shadow-md transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
              >
                Quit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Countdown overlay */}
      <Countdown
        isActive={showCountdown}
        onComplete={handleCountdownComplete}
      />

      {/* Goal screen overlay */}
      {showGoalScreen && (
        <div
          className={`pointer-events-auto fixed inset-0 z-30 flex items-center justify-center bg-black/70 px-4 py-8 transition-opacity duration-300 ${isDismissingGoal ? 'opacity-0' : 'opacity-100'}`}
          onTransitionEnd={handleGoalScreenTransitionEnd}
        >
          <div className="w-full max-w-md rounded-2xl bg-white/10 p-6 shadow-2xl ring-1 ring-white/10 backdrop-blur-lg">
            {/* Level indicator (subtle, informational) */}
            <div className="text-center text-xs font-medium uppercase tracking-[0.22em] text-white/50">
              LEVEL {levelIndex} OF {totalLevels}
            </div>
            <div className="text-center text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
              Your Goal
            </div>
            <h2 className="mt-2 text-center text-3xl font-semibold text-white">
              Guide the Buddha
            </h2>
            <p className="mt-4 text-center text-base leading-relaxed text-white/80">
              Breathe with the rhythm to help the Buddha rise toward the cloud.
              Complete inhale and exhale cycles to lift him gently upward. Stay
              focused - missed timing causes him to drift downward.
            </p>
            <p className="mt-4 text-center text-sm leading-relaxed text-white/70">
              <strong className="text-white">Controls:</strong> Press and hold
              the Spacebar, touch and hold the game area on mobile, or click &
              hold to inhale; release to exhale.
            </p>

            <button
              type="button"
              onClick={handleBegin}
              className="mt-6 min-h-[56px] w-full rounded-xl bg-gradient-to-r from-amber-400/90 to-orange-400/90 px-6 py-3 text-lg font-semibold uppercase tracking-wide text-white shadow-lg transition-transform duration-200 hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-amber-300/60"
            >
              Begin
            </button>
          </div>
        </div>
      )}

      {/* Pause controls are in the progress overlay; Reset button removed */}

      {/* Main UI container - positioned near top for visibility */}
      {shouldShowBreathingUI && showBreathingOverlay && (
        <div className="flex flex-col items-center gap-4 pt-8 sm:pt-12">
          {/* Phase label - the 3D ring is now in the scene, so we only show text here */}
          <BreathPhaseLabel
            currentPhase={currentPhase}
            isActive={breathingUIActive}
          />
        </div>
      )}

      {/* Instructions - positioned at the bottom */}
      {shouldShowBreathingUI && showBreathingOverlay && (
        <div className="mt-auto pb-8 sm:pb-12">
          <Instructions
            cyclesCompleted={cyclesCompleted}
            isActive={breathingUIActive}
          />
        </div>
      )}

      {/* Completion modal */}
      {breathingState.sessionComplete && completionModalReady && (
        <CompletionModal
          level={levelIndex}
          has14DayStreakBadge={has14DayStreakBadge}
          badgesLoading={userBadgesLoading}
          onViewBadges={handleViewBadges}
          onRestartLevel1={handleReplayLevel1}
          onPlayLevel2={handlePlayLevel2}
          onPlayLevel3={handlePlayLevel3}
        />
      )}

      {/* Debug toggle removed */}
    </div>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export { default as BreathPhaseLabel } from './BreathPhaseLabel';
export { default as Instructions } from './Instructions';
